import { KeyValuePair, PartialReport, ReportBase, ReportDictionary, ReportingAgentStates, ReportingMediaType, ReportingMediaTypes } from "../models/reports";
import { AgentReport, HandledWorkItem, RawAgentLiveData, RawAgentTodayData, RawWorkItemData, WorkItemHandlingStats } from "../models/reports.agents";
import getWorkItemInfoFromMainConversation from "./getWorkItemInfoFromMainConversation";

const defaultTimeSpentInStates: AgentReport["timeSpentInStates"] = ReportingAgentStates
  .reduce((coll, state) => ({ ...coll, [state]: 0 }), {} as AgentReport["timeSpentInStates"]);

const defaultWorkItemsByMedia: AgentReport["workItemsByMedia"] = ReportingMediaTypes
  .reduce((coll, media) => ({ ...coll, [media]: { handledCount: 0, missedCount: 0, rejectedCount: 0, averageHandlingTimeInSeconds: 0 } }), {} as AgentReport["workItemsByMedia"]);

export const defaultAgentReport = (id: string): AgentReport => ({
  id,
  name: "",
  tenantId: "",
  currentState: "LoggedOut",
  breakName: null,
  currentStateSince: new Date(),
  timeSpentInStates: defaultTimeSpentInStates,
  timeSpentInBreakNames: {},
  workItemsByBusinessUnit: {},
  workItemsByMedia: defaultWorkItemsByMedia,
  workItemsByDispositionCode: {},
  currentlyHandledWorkItems: [],
  loggedInAt: null,
  loggedOutAt: null,
});

const reduceToObject = <TValue, TRes>(arr: KeyValuePair<TValue>[], selector: (value: TValue) => unknown = v => v): Record<string, TRes> =>
  arr.reduce((coll, { key, value }) => ({ ...coll, [key]: selector(value) }), {});

export function processAgentTodayData(agentToday: RawAgentTodayData): PartialReport<AgentReport> {
  return {
    id: agentToday.id,
    ...agentToday.name ? { name: agentToday.name } : {},
    tenantId: agentToday.tenantId,
    timeSpentInStates: {
      ...ReportingAgentStates.reduce((coll, key) => ({ ...coll, [key]: 0 }), {} as AgentReport["timeSpentInStates"]),
      ...reduceToObject(agentToday.secondsSpentPerState),
    },
    timeSpentInBreakNames: reduceToObject(agentToday.secondsSpentPerBreakName),
    workItemsByBusinessUnit: getWorkItemsByBusinessUnit(agentToday),
    workItemsByMedia: getWorkItemsByMedia(agentToday),
    workItemsByDispositionCode: reduceToObject(agentToday.workItemKpisPerDispositionCode, val => val.handledCount),
    loggedInAt: agentToday.loggedInAt ? new Date(agentToday.loggedInAt) : null,
    loggedOutAt: agentToday.loggedOutAt ? new Date(agentToday.loggedOutAt) : null,
  };
}

export function processAgentLiveData(agentState: RawAgentLiveData): PartialReport<AgentReport> {
  return {
    id: agentState.agentId,
    ...agentState.agentName ? { name: agentState.agentName } : {},
    currentState: agentState.state,
    currentStateSince: new Date(agentState.stateSince),
    breakName: agentState.breakName,
  };
}

export function processWorkItemData(workItem: RawWorkItemData): HandledWorkItem {
  const mainConversationInfo = getWorkItemInfoFromMainConversation(workItem);

  return {
    primaryConversationId: mainConversationInfo?.conversationId ?? null,
    channelType: mainConversationInfo?.channelType ?? workItem.status,
    virtualConversationType: mainConversationInfo?.virtualConversationType ?? null,
    createdAt: new Date(workItem.createdAt),
    customerName: mainConversationInfo?.customerName ?? "",
    state: workItem.status,
  };
}

export function toReportDictionary<T extends ReportBase>(reports: T[]): ReportDictionary<T> {
  const dict: ReportDictionary<T> = {};
  reports.forEach(r => dict[r.id] = r);

  return dict;
}

interface MediaStatSet {
  avgHandlingTime: Record<string, number>;
  handled: Record<string, number>;
  missed?: Record<string, number>;
  rejected?: Record<string, number>;
}

function getWorkItemsByMedia(at: RawAgentTodayData): Record<ReportingMediaType, WorkItemHandlingStats> {
  function getHandlingStats(mediaStatSets: MediaStatSet[]): WorkItemHandlingStats {
    const businessUnitIds = new Set<string>();
    mediaStatSets.forEach((set) => {
      Object.values(set).forEach((statsByBusinessUnit) => {
        // I have no idea why TS can't figure out the type for statsByBusinessUnit here
        Object.keys(statsByBusinessUnit as Record<string, number>).forEach((businessUnitId) => {
          businessUnitIds.add(businessUnitId);
        });
      });
    });

    let totalHandled = 0;
    let totalMissed = 0;
    let totalRejected = 0;
    let totalHandlingTimeInSeconds = 0;

    businessUnitIds.forEach((businessUnitId) => {
      mediaStatSets.forEach((set) => {
        totalHandled += set.handled[businessUnitId] ?? 0;
        totalMissed += set.missed?.[businessUnitId] ?? 0;
        totalRejected += set.rejected?.[businessUnitId] ?? 0;
        totalHandlingTimeInSeconds += (set.avgHandlingTime[businessUnitId] ?? 0) * (set.handled[businessUnitId] ?? 0);
      });
    });

    return {
      handledCount: totalHandled,
      missedCount: totalMissed,
      rejectedCount: totalRejected,
      averageHandlingTimeInSeconds: totalHandled ? totalHandlingTimeInSeconds / totalHandled : 0,
    };
  }

  return {
    Chat: getHandlingStats([
      { avgHandlingTime: at.avgInboundChatHandlingTimeInSecondsPerBusinessUnit, handled: at.handledInboundChatTaskCountPerBusinessUnit, missed: at.missedInboundChatTaskCountPerBusinessUnit, rejected: at.rejectedInboundChatTaskCountPerBusinessUnit },
      { avgHandlingTime: at.avgOutboundChatHandlingTimeInSecondsPerBusinessUnit, handled: at.handledOutboundChatTaskCountPerBusinessUnit },
    ]),
    Email: getHandlingStats([
      { avgHandlingTime: at.avgInboundEmailHandlingTimeInSecondsPerBusinessUnit, handled: at.handledInboundEmailTaskCountPerBusinessUnit, missed: at.missedInboundEmailTaskCountPerBusinessUnit, rejected: at.rejectedInboundEmailTaskCountPerBusinessUnit },
      { avgHandlingTime: at.avgOutboundEmailHandlingTimeInSecondsPerBusinessUnit, handled: at.handledOutboundEmailTaskCountPerBusinessUnit },
    ]),
    Voice: getHandlingStats([
      { avgHandlingTime: at.avgInboundVoiceHandlingTimeInSecondsPerBusinessUnit, handled: at.handledInboundVoiceTaskCountPerBusinessUnit, missed: at.missedInboundVoiceTaskCountPerBusinessUnit, rejected: at.rejectedInboundVoiceTaskCountPerBusinessUnit },
      { avgHandlingTime: at.avgOutboundVoiceHandlingTimeInSecondsPerBusinessUnit, handled: at.handledOutboundVoiceTaskCountPerBusinessUnit },
      { avgHandlingTime: at.avgCampaignHandlingTimeInSecondsPerBusinessUnit, handled: at.handledCampaignTaskCountPerBusinessUnit, missed: at.missedCampaignTaskCountPerBusinessUnit, rejected: at.rejectedCampaignTaskCountPerBusinessUnit },
    ]),
    External: getHandlingStats([
      { avgHandlingTime: at.avgExternalTaskTimeInSecondsPerBusinessUnit, handled: at.externalTaskCountPerBusinessUnit },
    ]),
  };
}

function getWorkItemsByBusinessUnit(agentToday: RawAgentTodayData): Record<string, WorkItemHandlingStats> {
  const perBusinessUnitPropertyKeys = (Object.keys(agentToday) as (keyof RawAgentTodayData)[])
    .filter(key => key.endsWith("PerBusinessUnit") && typeof agentToday[key] === "object");

  const allBusinessUnitIds = new Set<string>();
  perBusinessUnitPropertyKeys.forEach((key) => {
    const businessUnitData = agentToday[key] as Record<string, number>;
    Object.keys(businessUnitData).forEach(businessUnitId => allBusinessUnitIds.add(businessUnitId));
  });

  const workItemsByBusinessUnit: Record<string, WorkItemHandlingStats> = {};
  allBusinessUnitIds.forEach((businessUnitId) => {
    function valueFrom(key: keyof RawAgentTodayData): number {
      const businessUnitData = agentToday[key] as Record<string, number>;
      return businessUnitData[businessUnitId] ?? 0;
    }

    const handledCount = 0
      + valueFrom("handledInboundVoiceTaskCountPerBusinessUnit")
      + valueFrom("handledInboundChatTaskCountPerBusinessUnit")
      + valueFrom("handledInboundEmailTaskCountPerBusinessUnit")
      + valueFrom("handledOutboundVoiceTaskCountPerBusinessUnit")
      + valueFrom("handledOutboundChatTaskCountPerBusinessUnit")
      + valueFrom("handledOutboundEmailTaskCountPerBusinessUnit")
      + valueFrom("handledCampaignTaskCountPerBusinessUnit")
      + valueFrom("externalTaskCountPerBusinessUnit");

    const missedCount = 0
      + valueFrom("missedInboundVoiceTaskCountPerBusinessUnit")
      + valueFrom("missedInboundChatTaskCountPerBusinessUnit")
      + valueFrom("missedInboundEmailTaskCountPerBusinessUnit")
      + valueFrom("missedCampaignTaskCountPerBusinessUnit");

    const rejectedCount = 0
      + valueFrom("rejectedInboundVoiceTaskCountPerBusinessUnit")
      + valueFrom("rejectedInboundChatTaskCountPerBusinessUnit")
      + valueFrom("rejectedInboundEmailTaskCountPerBusinessUnit")
      + valueFrom("rejectedCampaignTaskCountPerBusinessUnit");

    /* eslint-disable @stylistic/no-multi-spaces */
    const totalHandlingTimeInSeconds = 0
      + (valueFrom("avgInboundVoiceHandlingTimeInSecondsPerBusinessUnit")  * valueFrom("handledInboundVoiceTaskCountPerBusinessUnit"))
      + (valueFrom("avgInboundChatHandlingTimeInSecondsPerBusinessUnit")   * valueFrom("handledInboundChatTaskCountPerBusinessUnit"))
      + (valueFrom("avgInboundEmailHandlingTimeInSecondsPerBusinessUnit")  * valueFrom("handledInboundEmailTaskCountPerBusinessUnit"))
      + (valueFrom("avgOutboundVoiceHandlingTimeInSecondsPerBusinessUnit") * valueFrom("handledOutboundVoiceTaskCountPerBusinessUnit"))
      + (valueFrom("avgOutboundChatHandlingTimeInSecondsPerBusinessUnit")  * valueFrom("handledOutboundChatTaskCountPerBusinessUnit"))
      + (valueFrom("avgOutboundEmailHandlingTimeInSecondsPerBusinessUnit") * valueFrom("handledOutboundEmailTaskCountPerBusinessUnit"))
      + (valueFrom("avgCampaignHandlingTimeInSecondsPerBusinessUnit")      * valueFrom("handledCampaignTaskCountPerBusinessUnit"))
      + (valueFrom("avgExternalTaskTimeInSecondsPerBusinessUnit")          * valueFrom("externalTaskCountPerBusinessUnit"));
    /* eslint-enable @stylistic/no-multi-spaces */

    const averageHandlingTimeInSeconds = handledCount > 0 ? Math.round(totalHandlingTimeInSeconds / handledCount) : 0;
    workItemsByBusinessUnit[businessUnitId] = { handledCount, missedCount, rejectedCount, averageHandlingTimeInSeconds };
  });

  return workItemsByBusinessUnit;
}
